// shortcuts-model.ts — PSTUDIO-3623. The single source of the shortcuts the
// help panel renders. It is the EXACT mirror of the real hotkeys(...) in
// table-store.ts + the capture-phase handlers in table.tsx — a shortcut lives
// here iff it is actually wired. Phantom ⌘N is intentionally absent. Notation
// per the ticket/Figma: Mac modifiers as symbols (⌘⇧⌥⌃), arrows as ↑↓←→,
// Space/Esc/Tab/Enter/Delete spelled out; Windows all-text with " + " joins.
export interface ShortcutRow {
  id: string;
  descriptionKey: string; // i18n key under the keyboard-shortcuts namespace
  mac: string;
  windows: string;
}
export interface ShortcutSection {
  id: string;
  titleKey: string;
  rows: ShortcutRow[];
}

export const SHORTCUT_SECTIONS: ShortcutSection[] = [
  {
    id: 'help',
    titleKey: 'sections.help',
    rows: [
      {
        id: 'openPanel',
        descriptionKey: 'rows.openPanel',
        mac: '⌘/',
        windows: 'Ctrl + /',
      },
    ],
  },
  {
    id: 'navigation',
    titleKey: 'sections.navigation',
    rows: [
      {
        id: 'moveCells',
        descriptionKey: 'rows.moveCells',
        mac: '↑ ↓ ← →',
        windows: '↑ ↓ ← →',
      },
      {
        id: 'jumpColumn',
        descriptionKey: 'rows.jumpColumn',
        mac: '⌘↑ / ⌘↓',
        windows: 'Ctrl + ↑ / Ctrl + ↓',
      },
      {
        id: 'jumpRow',
        descriptionKey: 'rows.jumpRow',
        mac: '⌘← / ⌘→',
        windows: 'Ctrl + ← / Ctrl + →',
      },
      {
        id: 'toggleCell',
        descriptionKey: 'rows.toggleCell',
        mac: 'Space',
        windows: 'Spacebar',
      },
      {
        id: 'deselect',
        descriptionKey: 'rows.deselect',
        mac: 'Esc',
        windows: 'Esc',
      },
      {
        id: 'filterNode',
        descriptionKey: 'rows.filterNode',
        mac: '⌘G',
        windows: 'Ctrl + G',
      },
    ],
  },
  {
    id: 'selection',
    titleKey: 'sections.selection',
    rows: [
      {
        id: 'selectAll',
        descriptionKey: 'rows.selectAll',
        mac: '⌘A',
        windows: 'Ctrl + A',
      },
      {
        id: 'selectRow',
        descriptionKey: 'rows.selectRow',
        mac: '⇧Space',
        windows: 'Shift + Space',
      },
      {
        id: 'extendSelection',
        descriptionKey: 'rows.extendSelection',
        mac: '⇧↑ ⇧↓ ⇧← ⇧→',
        windows: 'Shift + ↑ ↓ ← →',
      },
    ],
  },
  {
    id: 'editing',
    titleKey: 'sections.editing',
    rows: [
      {
        id: 'enterEdit',
        descriptionKey: 'rows.enterEdit',
        mac: 'Enter',
        windows: 'Enter',
      },
      {
        id: 'commitNext',
        descriptionKey: 'rows.commitNext',
        mac: 'Tab',
        windows: 'Tab',
      },
      {
        id: 'expandedCellView',
        descriptionKey: 'rows.expandedCellView',
        mac: '⇧Enter',
        windows: 'Shift + Enter',
      },
      {
        id: 'clearCell',
        descriptionKey: 'rows.clearCell',
        mac: 'Delete',
        windows: 'Delete',
      },
    ],
  },
  {
    // PSTUDIO-1433: the JSON editor inside the Expanded Cell View has its own
    // key contract (product decision, 25 Aug 2026) — Enter commits there rather
    // than inserting a newline, and Tab indents rather than moving to the next
    // cell. Both differ from the grid rows above, so they get their own section
    // instead of silently contradicting them. Contract:
    // `src/stores/KEYBOARD_CLIPBOARD_BEHAVIOR.md`.
    id: 'expandedCellEditor',
    titleKey: 'sections.expandedCellEditor',
    rows: [
      {
        id: 'panelCommit',
        descriptionKey: 'rows.panelCommit',
        mac: 'Enter',
        windows: 'Enter',
      },
      {
        id: 'panelNewLine',
        descriptionKey: 'rows.panelNewLine',
        mac: '⌘Enter',
        windows: 'Ctrl + Enter',
      },
      {
        id: 'panelIndent',
        descriptionKey: 'rows.panelIndent',
        mac: 'Tab / ⇧Tab',
        windows: 'Tab / Shift + Tab',
      },
      {
        id: 'panelTabFocus',
        descriptionKey: 'rows.panelTabFocus',
        // Ctrl on BOTH platforms: the binding has no `mac:` variant, because
        // CodeMirror never delivers a mac Alt-letter chord (see
        // `json-editor-keymap.ts`). Showing ⇧⌥M here would advertise a
        // shortcut that does nothing on the machine reading it.
        mac: '⌃M',
        windows: 'Ctrl + M',
      },
    ],
  },
  {
    id: 'clipboard',
    titleKey: 'sections.clipboard',
    rows: [
      {
        id: 'copy',
        descriptionKey: 'rows.copy',
        mac: '⌘C',
        windows: 'Ctrl + C',
      },
      { id: 'cut', descriptionKey: 'rows.cut', mac: '⌘X', windows: 'Ctrl + X' },
      {
        id: 'paste',
        descriptionKey: 'rows.paste',
        mac: '⌘V',
        windows: 'Ctrl + V',
      },
    ],
  },
  {
    id: 'undoRedo',
    titleKey: 'sections.undoRedo',
    rows: [
      {
        id: 'autoRenumber',
        descriptionKey: 'rows.autoRenumber',
        mac: '⇧⌘S',
        windows: 'Ctrl + Shift + S',
      },
      {
        id: 'undo',
        descriptionKey: 'rows.undo',
        mac: '⌘Z',
        windows: 'Ctrl + Z',
      },
      {
        id: 'redo',
        descriptionKey: 'rows.redo',
        mac: '⇧⌘Z',
        windows: 'Ctrl + Shift + Z (Ctrl + Y also works)',
      },
    ],
  },
];

export interface ResolvedRow {
  id: string;
  descriptionKey: string;
  keys: string;
}
export interface ResolvedSection {
  id: string;
  titleKey: string;
  rows: ResolvedRow[];
}

export function getShortcutsForPlatform(
  platform: 'mac' | 'windows'
): ResolvedSection[] {
  return SHORTCUT_SECTIONS.map((section) => ({
    id: section.id,
    titleKey: section.titleKey,
    rows: section.rows.map((r) => ({
      id: r.id,
      descriptionKey: r.descriptionKey,
      keys: platform === 'mac' ? r.mac : r.windows,
    })),
  }));
}
